import React, { useState } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { SecondAds, FirstAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "حاسبة المصفوفات: جمع وطرح وضرب العمليات الرياضية على المصفوفات";
const DESCRIPTION =
  "قم بإجراء عمليات مختلفة على المصفوفات مثل الجمع، الطرح، الضرب، التبديل، والمزيد باستخدام هذه الحاسبة.";
const KEYWORDS = "مصفوفة, عمليات, حاسبة, الجمع, الطرح, الضرب, التبديل";
const OG_URL = "https://do-calculate.com/calculator/ar/matrix-operations/";

// Matrix utility functions
const createEmptyMatrix = (size) => {
  return Array(size).fill(null).map(() => Array(size).fill(0));
};

const cloneMatrix = (matrix) => {
  return matrix.map(row => [...row]);
};

const addMatrices = (matrixA, matrixB) => {
  const size = matrixA.length;
  const result = createEmptyMatrix(size);
  for (let i = 0; i < size; i++) {
    for (let j = 0; j < size; j++) {
      result[i][j] = matrixA[i][j] + matrixB[i][j];
    }
  }
  return result;
};

const subtractMatrices = (matrixA, matrixB) => {
  const size = matrixA.length;
  const result = createEmptyMatrix(size);
  for (let i = 0; i < size; i++) {
    for (let j = 0; j < size; j++) {
      result[i][j] = matrixA[i][j] - matrixB[i][j];
    }
  }
  return result;
};

const multiplyMatrices = (matrixA, matrixB) => {
  const size = matrixA.length;
  const result = createEmptyMatrix(size);
  for (let i = 0; i < size; i++) {
    for (let j = 0; j < size; j++) {
      let sum = 0;
      for (let k = 0; k < size; k++) {
        sum += matrixA[i][k] * matrixB[k][j];
      }
      result[i][j] = sum;
    }
  }
  return result;
};

const transposeMatrix = (matrix) => {
  const size = matrix.length;
  const result = createEmptyMatrix(size);
  for (let i = 0; i < size; i++) {
    for (let j = 0; j < size; j++) {
      result[j][i] = matrix[i][j];
    }
  }
  return result;
};

const determinant = (matrix) => {
  const size = matrix.length;

  if (size === 1) {
    return matrix[0][0];
  }

  if (size === 2) {
    return matrix[0][0] * matrix[1][1] - matrix[0][1] * matrix[1][0];
  }

  let det = 0;
  for (let j = 0; j < size; j++) {
    det += matrix[0][j] * cofactor(matrix, 0, j);
  }
  return det;
};

const cofactor = (matrix, row, col) => {
  const minor = getMinor(matrix, row, col);
  const sign = ((row + col) % 2 === 0) ? 1 : -1;
  return sign * determinant(minor);
};

const getMinor = (matrix, row, col) => {
  const size = matrix.length;
  const minor = [];
  for (let i = 0; i < size; i++) {
    if (i === row) continue;
    const minorRow = [];
    for (let j = 0; j < size; j++) {
      if (j === col) continue;
      minorRow.push(matrix[i][j]);
    }
    minor.push(minorRow);
  }
  return minor;
};

const inverseMatrix = (matrix) => {
  const size = matrix.length;
  const det = determinant(matrix);

  if (Math.abs(det) < 1e-10) {
    throw new Error("المصفوفة غير قابلة للعكس (المحدد = 0)");
  }

  if (size === 1) {
    return [[1 / matrix[0][0]]];
  }

  if (size === 2) {
    return [
      [matrix[1][1] / det, -matrix[0][1] / det],
      [-matrix[1][0] / det, matrix[0][0] / det]
    ];
  }

  // For larger matrices, use adjugate method
  const adjugate = createEmptyMatrix(size);
  for (let i = 0; i < size; i++) {
    for (let j = 0; j < size; j++) {
      adjugate[j][i] = cofactor(matrix, i, j);
    }
  }

  const inverse = createEmptyMatrix(size);
  for (let i = 0; i < size; i++) {
    for (let j = 0; j < size; j++) {
      inverse[i][j] = adjugate[i][j] / det;
    }
  }

  return inverse;
};

const traceMatrix = (matrix) => {
  const size = matrix.length;
  let trace = 0;
  for (let i = 0; i < size; i++) {
    trace += matrix[i][i];
  }
  return trace;
};

const formatNumber = (num) => {
  if (typeof num === 'number') {
    return Number(num.toFixed(4));
  }
  return num;
};

function MatrixOperationsCalculator() {
  const [matrixSize, setMatrixSize] = useState(2);
  const [matrixA, setMatrixA] = useState(createEmptyMatrix(2));
  const [matrixB, setMatrixB] = useState(createEmptyMatrix(2));
  const [resultMatrix, setResultMatrix] = useState(null);
  const [operation, setOperation] = useState("add");
  const [error, setError] = useState("");

  const handleSizeChange = (size) => {
    setMatrixSize(size);
    setMatrixA(createEmptyMatrix(size));
    setMatrixB(createEmptyMatrix(size));
    setResultMatrix(null);
  };

  const handleMatrixChange = (matrixSetter, rowIndex, colIndex, value) => {
    matrixSetter((prevMatrix) => {
      const updatedMatrix = cloneMatrix(prevMatrix);
      updatedMatrix[rowIndex][colIndex] = parseFloat(value) || 0;
      return updatedMatrix;
    });
  };

  const performOperation = () => {
    try {
      setError("");
      let result;
      switch (operation) {
        case "add":
          result = addMatrices(matrixA, matrixB);
          break;
        case "subtract":
          result = subtractMatrices(matrixA, matrixB);
          break;
        case "multiply":
          result = multiplyMatrices(matrixA, matrixB);
          break;
        case "transposeA":
          result = transposeMatrix(matrixA);
          break;
        case "transposeB":
          result = transposeMatrix(matrixB);
          break;
        case "determinantA":
          result = determinant(matrixA);
          break;
        case "determinantB":
          result = determinant(matrixB);
          break;
        case "inverseA":
          result = inverseMatrix(matrixA);
          break;
        case "inverseB":
          result = inverseMatrix(matrixB);
          break;
        case "traceA":
          result = traceMatrix(matrixA);
          break;
        case "traceB":
          result = traceMatrix(matrixB);
          break;
        default:
          throw new Error("عملية غير صالحة.");
      }
      setResultMatrix(result);
    } catch (error) {
      setError(error.message);
    }
  };

  const renderMatrixOutput = (matrix) => {
    if (Array.isArray(matrix)) {
      return (
        <div>
          {matrix.map((row, rowIndex) => (
            <div key={`row-${rowIndex}`} className="d-flex">
              {row.map((val, colIndex) => (
                <div
                  key={`col-${rowIndex}-${colIndex}`}
                  className="border text-center p-2"
                  style={{ width: "50px" }}
                >
                  {formatNumber(val)}
                </div>
              ))}
            </div>
          ))}
        </div>
      );
    } else {
      return <pre>{formatNumber(matrix)}</pre>;
    }
  };

  return (
    <div className="rtl">
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ height: "50px" }}
          >
            <h1 style={{ fontSize: "17px" }}>{TITLE}</h1>
          </div>
          <div className="border border-light">
            <div className="row">
              <div>
                <p>
                  هذه الحاسبة هي أداة قوية وسهلة الاستخدام مصممة لإجراء مجموعة
                  واسعة من العمليات على المصفوفات. سواء كنت طالبًا في الرياضيات
                  أو مهندسًا أو تعمل في أي مجال يتطلب التعامل مع البيانات بشكل
                  مصفوفي، فإن هذه الحاسبة ستكون مساعدًا لا يقدر بثمن. توفر
                  الحاسبة وظائف أساسية مثل الجمع، الطرح، والضرب، بالإضافة إلى
                  ميزات متقدمة تشمل حساب المحدد، إيجاد المصفوفة العكسية، حساب
                  رتبة المصفوفة، وتحويل المصفوفة إلى صورة صفية أو عمودية. بفضل
                  واجهة المستخدم البسيطة، يمكنك بسهولة إدخال قيم المصفوفات
                  واختيار العملية المطلوبة، مع إمكانية التحقق من صحة النتائج
                  بشكل فوري. تجد هذه الحاسبة تطبيقات واسعة في العديد من
                  المجالات، بما في ذلك الرياضيات، الفيزياء، الهندسة، علوم
                  الكمبيوتر، والاقتصاد. على سبيل المثال، يمكن استخدامها لحساب
                  مساحة قطعة أرض مستطيلة الشكل أو حل نظام من ثلاث معادلات بثلاثة
                  مجاهيل. يمكنك أيضًا ضبط حجم المصفوفات بين 2×2 و5×5.
                </p>
                <hr />
              </div>
            </div>
            <MobileUnderCalAds />
            <div className="row">
              <div className="col-sm-12 col-md-7 calculatorStyle">
                <div className="p-3 mb-2 bg-success text-white">
                  <h2 style={{ fontSize: "16px" }}>{TITLE}</h2>
                </div>
                <div className="p-3">
                  <div className="form-group">
                    <label htmlFor="matrixSize">حجم المصفوفة:</label>
                    <select
                      id="matrixSize"
                      value={matrixSize}
                      onChange={(e) =>
                        handleSizeChange(parseInt(e.target.value, 10))
                      }
                      className="form-control"
                    >
                      <option value="2">2x2</option>
                      <option value="3">3x3</option>
                      <option value="4">4x4</option>
                      <option value="5">5x5</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="matrixA">المصفوفة A:</label>
                    {Array.from({ length: matrixSize }).map((_, rowIndex) => (
                      <div className="d-flex" key={`A-${rowIndex}`}>
                        {Array.from({ length: matrixSize }).map(
                          (_, colIndex) => (
                            <input
                              id="matrixA"
                              key={`A-${rowIndex}-${colIndex}`}
                              type="number"
                              value={matrixA[rowIndex][colIndex]}
                              onChange={(e) =>
                                handleMatrixChange(
                                  setMatrixA,
                                  rowIndex,
                                  colIndex,
                                  e.target.value
                                )
                              }
                              className="form-control"
                              style={{ width: "25%" }}
                            />
                          )
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="form-group">
                    <label htmlFor="matrixB">المصفوفة B:</label>
                    {Array.from({ length: matrixSize }).map((_, rowIndex) => (
                      <div className="d-flex" key={`B-${rowIndex}`}>
                        {Array.from({ length: matrixSize }).map(
                          (_, colIndex) => (
                            <input
                              id="matrixB"
                              key={`B-${rowIndex}-${colIndex}`}
                              type="number"
                              value={matrixB[rowIndex][colIndex]}
                              onChange={(e) =>
                                handleMatrixChange(
                                  setMatrixB,
                                  rowIndex,
                                  colIndex,
                                  e.target.value
                                )
                              }
                              className="form-control"
                              style={{ width: "25%" }}
                            />
                          )
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="form-group">
                    <label htmlFor="operation">العملية:</label>
                    <select
                      id="operation"
                      value={operation}
                      onChange={(e) => setOperation(e.target.value)}
                      className="form-control"
                    >
                      <option value="add">جمع المصفوفات</option>
                      <option value="subtract">طرح المصفوفات</option>
                      <option value="multiply">ضرب المصفوفات</option>
                      <option value="transposeA">تبديل المصفوفة A</option>
                      <option value="transposeB">تبديل المصفوفة B</option>
                      <option value="determinantA">محدد المصفوفة A</option>
                      <option value="determinantB">محدد المصفوفة B</option>
                      <option value="inverseA">معكوس المصفوفة A</option>
                      <option value="inverseB">معكوس المصفوفة B</option>
                      <option value="traceA">أثر المصفوفة A</option>
                      <option value="traceB">أثر المصفوفة B</option>
                    </select>
                  </div>
                  <br />
                  <button
                    className="btn btn-success"
                    style={{ fontSize: "20px", marginLeft: "10px" }}
                    onClick={performOperation}
                  >
                    احسب
                  </button>
                  {error ? (
                    <div className="p-3 mb-2 bg-warning text-white">
                      <p>خطأ: </p>
                      {error}
                    </div>
                  ) : (
                    resultMatrix !== null && (
                      <div
                        className="bg-success text-white"
                        style={{ paddingBottom: "10px", paddingRight: "10px" }}
                      >
                        <h5>النتيجة:</h5>
                        {renderMatrixOutput(resultMatrix)}
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
          </div>
          <hr />
          <div className="row">
            <RelatedLinks />
            <hr />
          </div>
        </div>
        <div className="col">
          <SecondAds />
          <br />
          <MainCalculatorsList />
          <br />
        </div>
      </div>
      <div className="row" style={{ height: "auto" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">تعليمات الاستخدام</div>
              <div className="info-section-body">
                <div>
                  <u>
                    <h2 style={{ fontSize: "16px" }}>
                      الخطوة 1: تحديد حجم المصفوفة
                    </h2>
                  </u>
                  <p>
                    حدد حجم المصفوفة باستخدام مربع الإدخال. يمكنك تحديد حجم
                    المصفوفة من 2×2 إلى 5×5. احرص على اختيار الحجم المناسب
                    للعملية التي تنوي القيام بها، مثل الجمع أو الطرح.
                  </p>
                  <br />
                </div>
                <hr />
                <div>
                  <u>
                    <h2 style={{ fontSize: "16px" }}>
                      الخطوة 2: إدخال عناصر المصفوفات A و B
                    </h2>
                  </u>
                  <p>
                    بعد تحديد حجم المصفوفة، قم بإدخال القيم في المصفوفات A و B.
                    استخدم مربعات الإدخال لإدخال الأرقام في كل عنصر من عناصر
                    المصفوفة. تأكد من إدخال القيم بدقة للحصول على نتائج صحيحة.
                    ستظهر المصفوفة على الشاشة كما أدخلتها تمامًا.
                  </p>
                  <br />
                </div>
                <hr />
                <div>
                  <u>
                    <h2 style={{ fontSize: "16px" }}>
                      الخطوة 3: اختيار العملية
                    </h2>
                  </u>
                  <p>
                    اختر العملية التي تريد القيام بها على المصفوفات من قائمة
                    الاختيارات. يمكنك اختيار الجمع، الطرح، الضرب، أو أي من
                    العمليات الأخرى المتاحة مثل التبديل، إيجاد المعكوس، أو حل
                    المعادلات. بمجرد اختيار العملية، اضغط على زر "حساب" لرؤية
                    النتائج.
                  </p>
                  <br />
                </div>
                <hr />
                <div>
                  <u>
                    <h2 style={{ fontSize: "16px" }}>الخطوة 4: عرض النتيجة</h2>
                  </u>
                  <p>
                    بعد الضغط على زر "حساب"، ستظهر النتيجة على شكل مصفوفة جديدة
                    (أو قيمة واحدة إذا كانت العملية تتطلب ذلك). يمكنك مراجعة
                    النتيجة في المربع المخصص للعرض والذي يظهر النتيجة بطريقة
                    منظمة وواضحة، مشابهة لعرض المصفوفات التقليدية.
                  </p>
                  <br />
                </div>
                <hr />
                <div>
                  <u>
                    <h2 style={{ fontSize: "16px" }}>
                      ما هي العمليات المتاحة؟
                    </h2>
                  </u>
                  هذه الحاسبة تقدم العديد من العمليات المتاحة التي يمكنك
                  استخدامها:
                  <ul>
                    <li>
                      جمع المصفوفات: تقوم بجمع العناصر المتناظرة في المصفوفات.
                    </li>
                    <li>
                      طرح المصفوفات: تقوم بطرح العناصر المتناظرة في المصفوفات.
                    </li>
                    <li>
                      ضرب المصفوفات: تقوم بضرب المصفوفات مع بعضها البعض.
                    </li>
                    <li>تبديل المصفوفة: تقوم بعكس الصفوف مع الأعمدة.</li>
                    <li>محدد المصفوفة: تحسب المحدد للمصفوفة المحددة.</li>
                    <li>
                      معكوس المصفوفة: تحسب معكوس المصفوفة إذا كانت قابلة
                      للعكس.
                    </li>
                    <li>
                      أثر المصفوفة: تحسب مجموع العناصر على القطر الرئيسي
                      للمصفوفة.
                    </li>
                    <li>
                      حل معادلة Ax = B: تقوم بحل النظام الخطي باستخدام
                      المصفوفات.
                    </li>
                  </ul>
                  <br />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MatrixOperationsCalculator;